/**
 * Value Objects - Immutable domain objects without identity
 * Using Zod 4.1.9 API
 * 
 * Value objects are compared by their values, not their identity.
 * They should be immutable and replaceable.
 */
import { z } from 'zod';
import {
  GradeLevel,
  ImmunizationStatus,
  ParentRelation,
  StudentGender,
} from '../types/enums';
import { EmailSchema, PhoneNumberSchema } from './core';

export {
  AddressSchema,
  type Address,
  EmailSchema,
  type Email,
  PhoneNumberSchema,
  type PhoneNumber,
  ContactInfoSchema,
  type ContactInfo,
} from './core';

// ============================================================================
// Parent Info
// ============================================================================

export const ParentInfoSchema = z.object({
  title: z.string().nullable().optional(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  hebrewFirstName: z.string().nullable().optional(),
  hebrewLastName: z.string().nullable().optional(),
  maidenName: z.string().nullable().optional(),
  email: EmailSchema.optional(),
  phone: PhoneNumberSchema.optional(),
  relation: ParentRelation.optional(),
  occupation: z.string().nullable().optional(),
  firm: z.string().nullable().optional(),
  isPrimary: z.boolean().optional(),
  isDeceasedOrNoLongerLegalGuardian: z.boolean().default(false),
});
export type ParentInfo = z.infer<typeof ParentInfoSchema>;

// ============================================================================
// Child Info
// ============================================================================

export const ChildInfoSchema = z.object({
  firstName: z.string().min(1),
  middleName: z.string().nullable().optional(),
  lastName: z.string().min(1),
  hebrewFirstName: z.string().nullable().optional(),
  hebrewMiddleName: z.string().nullable().optional(),
  hebrewLastName: z.string().nullable().optional(),
  calledBy: z.string().nullable().optional(),
  dateOfBirth: z.number().int(), // Unix timestamp (seconds)
  hebrewDateOfBirth: z.string().nullable().optional(),
  gender: StudentGender.optional(),
  currentGrade: GradeLevel.nullable().optional(),
  placeOfBirth: z.string().nullable().optional(),
  languageAtHome: z.string().nullable().optional(),
  currentSchool: z.string().nullable().optional(),
  principalName: z.string().nullable().optional(),
  principalPhone: z.string().nullable().optional(),
});
export type ChildInfo = z.infer<typeof ChildInfoSchema>;

// ============================================================================
// Health Info (Nurse Portal) - Basic medical info for application
// ============================================================================

export const HealthInfoSchema = z.object({
  allergies: z.array(z.string()).default([]),
  medications: z.array(z.string()).default([]),
  specialNeeds: z.string().nullable().optional(),
  physicianName: z.string().nullable().optional(),
  physicianPhone: z.string().nullable().optional(),
  dentistName: z.string().nullable().optional(),
  dentistPhone: z.string().nullable().optional(),
  insuranceProvider: z.string().nullable().optional(),
  insurancePolicyNumber: z.string().nullable().optional(),
});
export type HealthInfo = z.infer<typeof HealthInfoSchema>;

// ============================================================================
// Medical Record (v1.2: Enhanced for Enrollment Portal)
// More comprehensive than HealthInfo, includes chronic conditions
// ============================================================================

export const MedicalRecordSchema = z.object({
  allergies: z.array(z.string()).default([]),
  chronicConditions: z.array(z.string()).default([]),
  medications: z.array(z.string()).default([]),
  immunizationStatus: ImmunizationStatus.default('not_provided'),
  emergencyNotes: z.string().default(''),
});
export type MedicalRecord = z.infer<typeof MedicalRecordSchema>;

// ============================================================================
// Enrollment Progress (v1.2: Post-Acceptance Checklist)
// Tracks completion of enrollment steps per student
// ============================================================================

export const EnrollmentProgressSchema = z.object({
  // Step completion flags
  step1AcceptanceLetter: z.boolean().default(false),
  step2RegistrationForm: z.boolean().default(false),
  step3B6TForm: z.boolean().default(false),        // NJ Transportation form
  step4TuitionContract: z.boolean().default(false),
  step5Payment: z.boolean().default(false),
  step6ImmunizationRecords: z.boolean().default(false),
  
  // Timestamps for audit trail
  formSubmittedAt: z.number().int().nullable().optional(),   // Unix seconds
  contractSignedAt: z.number().int().nullable().optional(),
  paymentConfirmedAt: z.number().int().nullable().optional(),
  immunizationVerified: z.boolean().default(false),
});
export type EnrollmentProgress = z.infer<typeof EnrollmentProgressSchema>;

// ============================================================================
// Enrollment Step Data (v1.3: Structured payload persistence per checklist step)
// ============================================================================

export const AcceptanceLetterStepDataSchema = z.object({
  reviewedAt: z.number().int().nullable().optional(),
  acknowledgedBy: z.enum(['parent', 'staff']).optional(),
  notes: z.string().max(2000).nullable().optional(),
  documentId: z.string().nullable().optional(),
  documentResourceId: z.string().nullable().optional(),
});
export type AcceptanceLetterStepData = z.infer<typeof AcceptanceLetterStepDataSchema>;

export const TuitionContractStepDataSchema = z.object({
  contractVersion: z.string().max(255).nullable().optional(),
  contractStatus: z.enum(['draft', 'sent']).nullable().optional(),
  signerFullName: z.string().max(255).nullable().optional(),
  signedAt: z.number().int().nullable().optional(),
  signatureToken: z.string().max(4096).nullable().optional(),
  totalDueCents: z.number().int().nonnegative().nullable().optional(),
  installmentCount: z.number().int().nonnegative().nullable().optional(),
  paymentMethod: z.enum(['full', 'electronic', 'post_dated_checks', 'third_party_checks']).nullable().optional(),
  paymentPreferenceLabel: z.string().max(255).nullable().optional(),
  paymentAmountLabel: z.string().max(255).nullable().optional(),
  installmentAmountLabel: z.string().max(255).nullable().optional(),
  installmentStartDate: z.string().max(64).nullable().optional(),
  installmentEndDate: z.string().max(64).nullable().optional(),
  thirdPartyName: z.string().max(255).nullable().optional(),
  documentId: z.string().nullable().optional(),
  documentResourceId: z.string().nullable().optional(),
  termsAcknowledged: z.boolean().default(false),
});
export type TuitionContractStepData = z.infer<typeof TuitionContractStepDataSchema>;

export const TransportationB6tStepDataSchema = z.object({
  requiresTransportation: z.boolean(),
  amPickupMode: z.enum(['home', 'other']).default('home'),
  amPickupAddress: z.string().max(500).nullable().optional(),
  pmDropoffMode: z.enum(['home', 'other']).default('home'),
  pmDropoffAddress: z.string().max(500).nullable().optional(),
  specialInstructions: z.string().max(1000).nullable().optional(),
  signerFullName: z.string().max(255).nullable().optional(),
  signedAt: z.number().int().nullable().optional(),
});
export type TransportationB6tStepData = z.infer<typeof TransportationB6tStepDataSchema>;

export const RegistrationFeeStepDataSchema = z.object({
  amountPaidCents: z.number().int().nonnegative(),
  currency: z.string().min(3).max(3).default('USD'),
  paymentProvider: z.enum(['stripe', 'manual', 'other']).default('other'),
  transactionReference: z.string().max(255).nullable().optional(),
  receiptDocumentId: z.string().nullable().optional(),
  paidAt: z.number().int().nullable().optional(),
});
export type RegistrationFeeStepData = z.infer<typeof RegistrationFeeStepDataSchema>;

export const EnrollmentStepDataSchema = z.object({
  acceptanceLetter: AcceptanceLetterStepDataSchema.nullable().optional(),
  tuitionContract: TuitionContractStepDataSchema.nullable().optional(),
  transportationB6t: TransportationB6tStepDataSchema.nullable().optional(),
  registrationFee: RegistrationFeeStepDataSchema.nullable().optional(),
});
export type EnrollmentStepData = z.infer<typeof EnrollmentStepDataSchema>;

// ============================================================================
// Grandparent Info (v1.2: Simplified from detailed Grandparent)
// ============================================================================

export const GrandparentInfoSchema = z.object({
  names: z.string().default(''),           // Combined: "Mr. Solomon & Mrs. Esther"
  location: z.string().default(''),        // City, State
  phone: z.string().nullable().optional(),
  family: z.string().nullable().optional(), // Family name (e.g., "Gertler Family")
});
export type GrandparentInfo = z.infer<typeof GrandparentInfoSchema>;

// ============================================================================
// Sibling Info (v1.2 Enrollment Portal: other siblings not applying)
// ============================================================================

export const SiblingInfoSchema = z.object({
  id: z.string().nullable().optional(),
  name: z.string().min(1),
  age: z.string().nullable().optional(),
  school: z.string().nullable().optional(),
});
export type SiblingInfo = z.infer<typeof SiblingInfoSchema>;
